import type { FurniturePart, Vec2 } from "../../types";
import { clamp, ellipse, part, rect, resetPartIds } from "../geometry";
import { str, type BuildContext, type BuildResult, type FurnitureTemplate } from "./types";

export const tableTemplate: FurnitureTemplate = {
  id: "table",
  label: "Table",
  frontKind: "none",
  defaultMaterial: "wood-mid",
  params: [
    {
      id: "shape",
      label: "Shape",
      type: "enum",
      default: "rect",
      options: [
        { value: "rect", label: "Rectangle" },
        { value: "round", label: "Round" },
        { value: "oval", label: "Oval" },
      ],
    },
    {
      id: "legs",
      label: "Base",
      type: "enum",
      default: "corner",
      options: [
        { value: "corner", label: "Four legs" },
        { value: "pedestal", label: "Pedestal" },
        { value: "hidden", label: "None" },
      ],
    },
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const shape = str(params, "shape", "rect");
    const legs = str(params, "legs", "corner");
    const TOP = 1.5;
    const parts: FurniturePart[] = [];
    let footprint: Vec2[] | undefined;
    if (shape === "round" || shape === "oval") {
      const fp = ellipse(0, 0, w / 2, d / 2);
      footprint = fp;
      // Legs / pedestal peek out below the top only conceptually; from above
      // the top hides them. We still emit them (z below top) for a future
      // 3D view and for the translucent-glass case.
      if (legs === "pedestal") {
        parts.push(part("leg", ellipse(0, 0, w * 0.18, d * 0.18), 0, h - TOP));
      }
      parts.push(part("top", fp, h - TOP, h));
      return { parts, footprint };
    }
    const r = clamp(Math.min(w, d) * 0.05, 0.5, 2);
    if (legs === "corner") {
      const L = clamp(Math.min(w, d) * 0.08, 1.5, 3);
      const inset = 1.5;
      for (const [sx, sy] of [
        [-1, -1],
        [1, -1],
        [1, 1],
        [-1, 1],
      ] as const) {
        const x = sx < 0 ? -w / 2 + inset : w / 2 - inset - L;
        const y = sy < 0 ? -d / 2 + inset : d / 2 - inset - L;
        parts.push(part("leg", rect(x, y, L, L), 0, h - TOP));
      }
    } else if (legs === "pedestal") {
      parts.push(part("leg", rect(-w * 0.18, -d * 0.18, w * 0.36, d * 0.36), 0, h - TOP, { cornerRadius: 1 }));
    }
    parts.push(part("top", rect(-w / 2, -d / 2, w, d), h - TOP, h, { cornerRadius: r }));
    return { parts };
  },
};

export const deskTemplate: FurnitureTemplate = {
  id: "desk",
  label: "Desk",
  frontKind: "drawer",
  defaultMaterial: "wood-mid",
  params: [
    {
      id: "layout",
      label: "Layout",
      type: "enum",
      default: "straight",
      options: [
        { value: "straight", label: "Straight" },
        { value: "l-left", label: "L (left return)" },
        { value: "l-right", label: "L (right return)" },
      ],
    },
    {
      id: "pedestal",
      label: "Drawers",
      type: "enum",
      default: "right",
      options: [
        { value: "none", label: "None" },
        { value: "left", label: "Left" },
        { value: "right", label: "Right" },
        { value: "both", label: "Both" },
      ],
    },
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const layout = str(params, "layout", "straight");
    const pedestal = str(params, "pedestal", "right");
    const TOP = 1.5;
    const parts: FurniturePart[] = [];
    const x0 = -w / 2;
    const x1 = w / 2;
    const y0 = -d / 2;
    const y1 = d / 2;

    if (layout === "straight") {
      const top = rect(x0, y0, w, d);
      // Pedestal (drawer stack) boxes under the top; they're hidden from
      // above but define where the drawer front band sits.
      const PW = clamp(w * 0.28, 14, 20);
      const band = clamp(d * 0.06, 1, 1.5);
      if (pedestal === "left" || pedestal === "both") {
        parts.push(part("drawer-face", rect(x0 + 0.5, y1 - band - 0.5, PW, band), h - TOP - 0.01, h + 0.01));
      }
      if (pedestal === "right" || pedestal === "both") {
        parts.push(part("drawer-face", rect(x1 - PW - 0.5, y1 - band - 0.5, PW, band), h - TOP - 0.01, h + 0.01));
      }
      parts.unshift(part("top", top, h - TOP, h, { cornerRadius: 1 }));
      return { parts };
    }

    // L-desk: main run along the back (-y), return down one side toward +y.
    const MD = clamp(d * 0.45, 24, 30); // main run depth
    const RW = clamp(w * 0.45, 20, 30); // return width
    const right = layout === "l-right";
    const footprint: Vec2[] = right
      ? [
          { x: x0, y: y0 },
          { x: x1, y: y0 },
          { x: x1, y: y1 },
          { x: x1 - RW, y: y1 },
          { x: x1 - RW, y: y0 + MD },
          { x: x0, y: y0 + MD },
        ]
      : [
          { x: x0, y: y0 },
          { x: x1, y: y0 },
          { x: x1, y: y0 + MD },
          { x: x0 + RW, y: y0 + MD },
          { x: x0 + RW, y: y1 },
          { x: x0, y: y1 },
        ];
    parts.push(part("top", footprint, h - TOP, h));
    // Drawer band on the user-facing edge of the main run.
    const PW = clamp(w * 0.25, 14, 20);
    const band = 1.25;
    if (pedestal !== "none") {
      const px = right ? x0 + 0.5 : x1 - PW - 0.5;
      parts.push(part("drawer-face", rect(px, y0 + MD - band - 0.5, PW, band), h - TOP - 0.01, h + 0.01));
    }
    return { parts, footprint };
  },
};
